import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useLocalization } from '@/shared/composables/localization'
import {
  FeaturesNames,
  PackageOfServices,
  PermissionAndService,
  PermissionGroup,
  PermissionGroupUpdateItem,
  ServicesList,
} from '@/shared/types/settings'

import { useAPISettingsPermissionAndServices } from '../api/index'
import { buildServicesAndPermissionList } from '../lib'

export const useStoreSettingsServicesAndPackages = defineStore(
  'servicesAndPackages',
  () => {
    const permissionGroupLists = ref(new Map<string, PermissionGroup>())
    const servicesList = ref(new Map<string, ServicesList>())
    const packageOfServicesList = ref(new Map<string, PackageOfServices>())
    const servicesAndPermissionList = ref(new Map<string, PermissionAndService>())

    const isLoading = ref(false)
    const permissionGroupsLoadAttempted = ref(false)
    const servicesListLoadAttempted = ref(false)
    let permissionGroupInFlight: Promise<boolean> | null = null
    let servicesListInFlight: Promise<boolean> | null = null

    const areAccessFeaturesLoaded = computed(() => (
      permissionGroupsLoadAttempted.value && servicesListLoadAttempted.value
    ))

    const rebuildCombinedList = () => {
      servicesAndPermissionList.value = buildServicesAndPermissionList(
        Array.from(servicesList.value.values()),
        Array.from(permissionGroupLists.value.values()),
        Array.from(packageOfServicesList.value.values()),
      )
    }

    const loadPermissionGroup = () => {
      if (permissionGroupInFlight) return permissionGroupInFlight

      permissionGroupInFlight = new Promise<boolean>((resolve, reject) => {
        useAPISettingsPermissionAndServices()
          .getPermissionGroup()
          .then((result) => {
            if (result.status === 200 && Array.isArray(result.data)) {
              const next = new Map<string, PermissionGroup>()
              result.data.forEach((item) => {
                next.set(
                  item.guid,
                  Object.assign({ isUserServices: false }, item),
                )
              })
              permissionGroupLists.value = next
              resolve(true)
            } else {
              reject(new Error(`${result.status}${result.statusText}`))
            }
          })
          .catch((err) => {
            const { t } = useLocalization()
            reject(new Error(t('common.failedToLoadList', { err })))
          })
      }).finally(() => {
        permissionGroupsLoadAttempted.value = true
        permissionGroupInFlight = null
      })

      return permissionGroupInFlight
    }

    const loadServicesList = () => {
      if (servicesListInFlight) return servicesListInFlight

      servicesListInFlight = new Promise<boolean>((resolve, reject) => {
        useAPISettingsPermissionAndServices()
          .getServicesList()
          .then((result) => {
            if (result.status === 200 && Array.isArray(result.data)) {
              const next = new Map<string, ServicesList>()
              result.data.forEach((item) => {
                next.set(item.guid, item)
              })
              servicesList.value = next
              resolve(true)
            } else {
              reject(new Error(`${result.status}${result.statusText}`))
            }
          })
          .catch((err) => {
            const { t } = useLocalization()
            reject(new Error(t('common.failedToLoadList', { err })))
          })
      }).finally(() => {
        servicesListLoadAttempted.value = true
        servicesListInFlight = null
      })

      return servicesListInFlight
    }

    const loadAllPackageOfServices = () => {
      return new Promise((resolve, reject) => {
        useAPISettingsPermissionAndServices().getPackageOfServicesLists().then((result) => {
          if (result.status === 200 && Array.isArray(result.data)) {
            const next = new Map<string, PackageOfServices>()
            result.data.forEach((item: PackageOfServices) => {
              next.set(item.guid, item)
            })
            packageOfServicesList.value = next
            resolve(true)
            return
          } else {
            reject(new Error(`${result.status}${result.statusText}`))
          }
        }).catch((err) => {
          const { t } = useLocalization()
          reject(new Error(t('common.failedToLoadList', { err })))
          return
        })
      })
    }

    const loadAllServicesAndPackage = async () => {
      isLoading.value = true
      try {
        await Promise.all([
          loadServicesList(),
          loadAllPackageOfServices(),
          loadPermissionGroup(),
        ])
        rebuildCombinedList()
      } finally {
        isLoading.value = false
      }
    }

    const refreshServicesAndPermissionList = async () => {
      await Promise.all([
        loadServicesList(),
        loadAllPackageOfServices(),
        loadPermissionGroup(),
      ])
      rebuildCombinedList()
    }

    const whenAccessFeaturesReady = async () => {
      await Promise.all([
        permissionGroupsLoadAttempted.value
          ? Promise.resolve()
          : loadPermissionGroup().catch(() => undefined),
        servicesListLoadAttempted.value
          ? Promise.resolve()
          : loadServicesList().catch(() => undefined),
      ])
    }

    const updateCombinedAclEnabled = (guid: string, aclEnabled: boolean) => {
      servicesAndPermissionList.value.forEach((item) => {
        if (item.guid !== guid) return
        item.aclEnabled = aclEnabled
      })
    }

    const syncServicePresence = (guid: string, aclEnabled: boolean) => {
      const combined = Array.from(servicesAndPermissionList.value.values())
        .find((item) => item.guid === guid)
      const name = combined?.name
      if (!name) return

      if (!aclEnabled) {
        const next = new Map(servicesList.value)
        next.forEach((service, key) => {
          if (service.name === name) next.delete(key)
        })
        servicesList.value = next
        return
      }

      const exists = Array.from(servicesList.value.values()).some((service) => service.name === name)
      if (exists) return

      const next = new Map(servicesList.value)
      next.set(guid, {
        guid,
        id: guid,
        name: name as FeaturesNames,
        isDefault: false,
        dstResult: combined.dstResult ?? '0',
        isUserServices: false,
      })
      servicesList.value = next
    }

    const updatePermissionItem = (updateItem: PermissionGroupUpdateItem): Promise<boolean> => {
      return new Promise((resolve, reject) => {
        useAPISettingsPermissionAndServices().updatePermissionGroup(updateItem).then((result) => {
          if (result.status === 204) {
            const tmpItem: PermissionGroup | undefined = permissionGroupLists.value.get(updateItem.guid)
            if (tmpItem) {
              tmpItem.aclEnabled = updateItem.aclEnabled ?? false
              permissionGroupLists.value.set(updateItem.guid, tmpItem)
            }
            updateCombinedAclEnabled(updateItem.guid, updateItem.aclEnabled ?? false)
            syncServicePresence(updateItem.guid, updateItem.aclEnabled ?? false)

            resolve(true)
          } else {
            reject(new Error(`${result.status}${result.statusText}`))
          }
        }).catch((err) => {
          reject(err)
        }).finally(() => {
          isLoading.value = false
        })
      })
    }

    const updateServicesPackage = (updateItem: { guid: string, enabled: boolean }) => {
      isLoading.value = true
      useAPISettingsPermissionAndServices().updatePackageOfServices(updateItem).then((result) => {
        if (result.status === 204) {
          const tmpItem: PackageOfServices | undefined = packageOfServicesList.value.get(updateItem.guid)
          if (tmpItem) {
            tmpItem.enabled = updateItem.enabled ?? false
            packageOfServicesList.value.set(updateItem.guid, tmpItem)
          }
        }
      }).catch((err) => {
        const { t } = useLocalization()
        throw new Error(t('common.failedToChangeElement', { err }))
      }).finally(() => {
        isLoading.value = false
      })
    }

    const getServicesListFromArray = computed(() => {
      return Array.from(servicesList.value, ([, value]) => value)
    })

    const getPackageOfServicesListFromArray = computed(() => {
      return Array.from(packageOfServicesList.value, ([, value]) => value)
    })

    const userIsEditableFeatures = computed(() => {
      const accessGroupManagement = Array.from(permissionGroupLists.value.values())
        .find((item) => item.name === FeaturesNames.ACCESS_GROUP_MANAGEMENT)

      if (accessGroupManagement) {
        return accessGroupManagement.aclEnabled === true
      }

      const packageWithAccessGroupManagement = Array.from(packageOfServicesList.value.values()).find(item => item.enabled && item.groupNames.includes(FeaturesNames.ACCESS_GROUP_MANAGEMENT))
      if (packageWithAccessGroupManagement) {
        return true
      }

      return false
    })

    const getEnabledFeatures = computed(() => {
      const enabledFeatures: Set<FeaturesNames> = new Set()
      servicesList.value.forEach((service: ServicesList) => {
        enabledFeatures.add(service.name)
      })
      permissionGroupLists.value.forEach((group) => {
        if (group.aclEnabled) {
          enabledFeatures.add(group.name)
        }
      })
      return enabledFeatures
    })

    return {
      isLoading,
      areAccessFeaturesLoaded,
      servicesAndPermissionList,
      getServicesListFromArray,
      getPackageOfServicesListFromArray,
      getEnabledFeatures,
      userIsEditableFeatures,
      loadAllServicesAndPackage,
      loadPermissionGroup,
      loadServicesList,
      updatePermissionItem,
      updateServicesPackage,
      refreshServicesAndPermissionList,
      loadAllPackageOfServices,
      whenAccessFeaturesReady,
    }
  },
)
